import apiService from 'services/apiService';
import Classroom from 'models/Classroom';
import TeacherGradebook from 'models/TeacherGradebook';
import Book from 'models/Book';
import Assignment from 'models/Assignment';
import Question from 'models/Question';
import AssignmentSubmission from 'models/AssignmentSubmission';
import {
  ServerMissedWords,
  Subscription,
  TeacherGeneratedStudent,
} from 'utils/types';
import StudentProfile from 'models/StudentProfile';
import { parseError, toServerDate } from 'utils/utils';
import User from 'models/User';
import { Cookies } from 'react-cookie';
import Module from 'models/Module';
import StudentQuestion from 'models/StudentQuestion';
import QuestionResponse from 'models/QuestionResponse';
import MissedWord from 'models/MissedWord';
import StudentMetrics from 'models/analytics/StudentMetrics';
import AssignmentFeedback from 'models/assignments/AssignmentFeedback';
import { gzipBlob } from 'utils/apiUtils';
import CachingEngine from 'utils/CachingEngine';

export class TeacherService {
  private static instance: TeacherService;
  private cookies: Cookies;

  constructor() {
    this.cookies = new Cookies();
  }

  public static get(): TeacherService {
    if (!TeacherService.instance) {
      TeacherService.instance = new TeacherService();
    }

    return TeacherService.instance;
  }

  getGradebook = async (classroom_id: string): Promise<TeacherGradebook> => {
    try {
      const response = await apiService.api.get(
        `/classrooms/${classroom_id}/gradebook/`,
      );

      return TeacherGradebook.fromServerGradebook(response.data);
    } catch (error) {
      console.error(error);
      return Promise.reject(new Error(parseError(error)));
    }
  };

  createClassroom = async (classroom: Classroom): Promise<Classroom> => {
    try {
      const response = await apiService.api.post('/classrooms/', {
        ...classroom,
      });

      return Classroom.fromServerClassroom(response.data);
    } catch (error) {
      console.error(error);
      return Promise.reject(new Error(parseError(error)));
    }
  };

  createAssignment = async (
    assignment: Assignment,
    questions: Question[],
    book_id?: string | number,
  ): Promise<Assignment> => {
    var respAssignment;
    try {
      const response = await apiService.api.post(
        '/assignments/',
        {
          ...assignment,
          book: book_id,
          start_date: assignment.start_date
            ? toServerDate(assignment.start_date)
            : null,
          due_date: assignment.due_date
            ? toServerDate(assignment.due_date)
            : null,
          google_access_token: this.cookies.get('googleAccessToken'),
          google_refresh_token: this.cookies.get('googleRefreshToken'),
        },
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        },
      );

      respAssignment = Assignment.fromServerAssignment(
        response.data.assignment,
      );

      for (let question of questions) {
        await apiService.api.post(
          `/assignmentquestions/`,
          {
            ...question.toJSON(),
            assignment: respAssignment.id,
          },
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          },
        );
      }

      return respAssignment;
    } catch (error) {
      console.error(error);
      if (!respAssignment) return Promise.reject(new Error(parseError(error)));
      else return respAssignment;
    }
  };

  createBook = async (
    book: Book,
    coverImageFile?: File,
    generateAudio?: boolean,
    shouldAnalyze: boolean = true,
    moduleId?: string,
  ): Promise<Book> => {
    try {
      const form = new FormData();

      // 1) append all your normal non-HTML fields
      const json = book.toJSON();
      Object.entries(json).forEach(([key, val]) => {
        if (val != null) form.append(key, String(val));
      });

      // 2) if we have HTML, gzip it into a Blob
      if (book.html_content) {
        // Update the cached book
        if ('CompressionStream' in window) {
          const gzBlob = await gzipBlob(book.html_content);
          form.append('html_gzip_file', gzBlob, 'content.html.gz');
          form.append('html_content_gzip', 'true');
        } else {
          // fallback for old browsers
          form.append('html_content', book.html_content);
        }
      }

      // 3) files + other flags
      if (coverImageFile) form.append('cover_image_file', coverImageFile);
      form.append('generate_audio', String(generateAudio));
      form.append('should_analyze', String(shouldAnalyze));
      if (moduleId) form.append('module', String(moduleId));

      const response = await apiService.api.post('/books/', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const respBook = Book.fromServerBook(response.data);
      CachingEngine.setData(`book-${respBook.id}`, book.html_content);

      return respBook;
    } catch (error) {
      console.error(error);
      return Promise.reject(new Error(parseError(error)));
    }
  };

  updateBook = async (
    book: Book,
    generateAudio?: boolean,
    coverImageFile?: File,
    moduleId?: string,
  ): Promise<void> => {
    try {
      // 1) build the FormData
      const form = new FormData();

      // append all non-HTML fields
      const json = book.toJSON();
      Object.entries(json).forEach(([key, val]) => {
        if (val != null) form.append(key, String(val));
      });

      // 2) gzip the HTML if present (CompressionStream fallback to raw)
      if (book.html_content) {
        CachingEngine.setData(`book-${book.id}`, book.html_content);
        if ('CompressionStream' in window) {
          const gzBlob = await gzipBlob(book.html_content);
          form.append('html_gzip_file', gzBlob, 'content.html.gz');
          form.append('html_content_gzip', 'true');
        } else {
          form.append('html_content', book.html_content);
        }
      }

      // 3) files + flags
      if (coverImageFile) form.append('cover_image_file', coverImageFile);
      form.append('generate_audio', String(generateAudio));
      form.append('should_analyze', 'true');
      if (moduleId) form.append('module', String(moduleId));

      // 4) send the PATCH
      await apiService.api.patch(`/books/${book.id}/`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    } catch (error) {
      console.error(error);
      return Promise.reject(new Error(parseError(error)));
    }
  };

  getClassroomAssignments = async (
    classroom: Classroom,
  ): Promise<Assignment[]> => {
    try {
      const response = await apiService.api.get(
        `/classrooms/${classroom.id}/assignments/`,
      );

      return response.data.map((respAssignment: Record<string, any>) =>
        Assignment.fromServerAssignment(respAssignment),
      );
    } catch (error) {
      console.error(error);
      return Promise.reject(new Error(parseError(error)));
    }
  };

  getAssignmentById = async (assignment_id: string): Promise<any> => {
    try {
      const response = await apiService.api.get(
        `/assignments/${assignment_id}/`,
      );

      var assignment = Assignment.fromServerAssignment(
        response.data.assignment,
      );

      var book;
      if (response.data.book) {
        book = Book.fromServerBook(response.data.book);
        await book.getHtmlContent();
      }

      return {
        assignment: assignment,
        book: book,
        questions: response.data.questions.map((respQ: Record<string, any>) =>
          Question.fromServerQuestion(respQ),
        ),
      };
    } catch (error) {
      console.error(error);
      return Promise.reject(new Error(parseError(error)));
    }
  };

  updateAssignment = async (
    assignment: Assignment,
    questions: Question[],
    book?: Book,
    coverImageFile?: File,
    generateAudio?: boolean,
  ): Promise<Assignment> => {
    try {
      const response = await apiService.api.put(
        `/assignments/${assignment.getId()}/`,
        {
          ...assignment,
          start_date: assignment.start_date
            ? toServerDate(assignment.start_date)
            : null,
          due_date: assignment.due_date
            ? toServerDate(assignment.due_date)
            : null,
          google_access_token: this.cookies.get('googleAccessToken'),
          google_refresh_token: this.cookies.get('googleRefreshToken'),
        },
      );
      if (book) {
        await this.updateBook(
          book,
          generateAudio,
          coverImageFile,
          assignment.module,
        );
      }
      for (let question of questions) {
        await apiService.api.patch(
          `/assignmentquestions/${question.id}/`,
          {
            ...question.toJSON(),
          },
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          },
        );
      }
      return Assignment.fromServerAssignment(response.data.assignment);
    } catch (error) {
      console.error(error);
      return Promise.reject(new Error(parseError(error)));
    }
  };

  updateAssignmentSubmission = async (
    submission: AssignmentSubmission,
  ): Promise<{
    submission: AssignmentSubmission;
    feedback: Record<string, any>[];
  }> => {
    try {
      const response = await apiService.api.patch(
        `/submissions/${submission.id}/`,
        {
          ...submission,
          google_access_token: this.cookies.get('googleAccessToken'),
          google_refresh_token: this.cookies.get('googleRefreshToken'),
        },
      );

      return {
        submission: AssignmentSubmission.fromServerAssignmentSubmission(
          response.data.submission,
        ),
        feedback: response.data.feedback ?? [],
      };
    } catch (error) {
      console.error(error);
      return Promise.reject(new Error(parseError(error)));
    }
  };

  createQuestion = async (
    text: string,
    index: number,
    assignment: Assignment,
    choices: any[],
  ): Promise<Question> => {
    try {
      const response = await apiService.api.post(
        `/assignmentquestions/`,
        {
          text: text,
          index: index,
          assignment: assignment.id,
          choices: choices?.map((c) => c.toJSON()),
        },
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        },
      );

      return Question.fromServerQuestion(response.data);
    } catch (error) {
      console.error(error);
      return Promise.reject(new Error(parseError(error)));
    }
  };

  updateQuestion = async (question: Question): Promise<Question> => {
    const response = await apiService.api.patch(
      `/assignmentquestions/${question.id}/`,
      question.toJSON(),
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    );

    return Question.fromServerQuestion({
      ...response.data,
      image_file: question.image_file,
    });
  };

  deleteQuestion = async (question: Question): Promise<boolean> => {
    try {
      await apiService.api.delete(`/assignmentquestions/${question.getId()}/`);
      return true;
    } catch (error) {
      console.error(error);
      return Promise.reject(new Error(parseError(error)));
    }
  };

  getClassroomMissedWords = async (
    classroom: Classroom,
  ): Promise<ServerMissedWords> => {
    try {
      const response = await apiService.api.get(
        `/classrooms/${classroom.id}/missed_words/`,
      );
      return response.data;
    } catch (error) {
      console.error(error);
      return Promise.reject(new Error(parseError(error)));
    }
  };

  getStudentMetrics = async (classroom: Classroom): Promise<StudentMetrics> => {
    try {
      const response = await apiService.api.get(
        `/classrooms/${classroom.id}/analytics/`,
      );
      return new StudentMetrics(response.data);
    } catch (error) {
      console.error(error);
      return Promise.reject(new Error(parseError(error)));
    }
  };

  updateClassroom = async (classroom: Classroom): Promise<Classroom> => {
    try {
      const response = await apiService.api.put(
        `/classrooms/${classroom.id}/`,
        { ...classroom },
      );

      return response.data;
    } catch (error) {
      console.error(error);
      return Promise.reject(new Error(parseError(error)));
    }
  };

  deleteClassroom = async (classroom: Classroom): Promise<boolean> => {
    try {
      await apiService.api.delete(`/classrooms/${classroom.id}/`);
      return true;
    } catch (error) {
      console.error(error);
      return Promise.reject(new Error(parseError(error)));
    }
  };

  updateStudentProfile = async (
    student: StudentProfile,
  ): Promise<StudentProfile> => {
    try {
      const response = await apiService.api.put(`/students/${student.id}/`, {
        ...student,
      });
      return response.data;
    } catch (error) {
      console.error(error);
      return Promise.reject(new Error(parseError(error)));
    }
  };

  deleteAssignment = async (assignmentId: string): Promise<boolean> => {
    try {
      await apiService.api.delete(`/assignments/${assignmentId}/`);
      return true;
    } catch (error) {
      console.error(error);
      return Promise.reject(new Error(parseError(error)));
    }
  };

  deleteStudent = async (student_id: string): Promise<boolean> => {
    try {
      await apiService.api.delete(`/students/${student_id}/`);
      return true;
    } catch (error) {
      console.error(error);
      return Promise.reject(new Error(parseError(error)));
    }
  };

  searchStudents = async (searchTerm: string) => {
    try {
      const response = await apiService.api.get(
        `/users/?search=${searchTerm}&account_type=Student`,
      );
      return response.data.map((respUser: Record<string, any>) =>
        User.fromServerUser(respUser),
      );
    } catch (error) {
      console.error(error);
      return Promise.reject(new Error(parseError(error)));
    }
  };

  addStudent = async (
    userId: string,
    classroomId: string,
    readingLevel: number,
  ): Promise<User> => {
    try {
      const response = await apiService.api.post(
        `/classrooms/${classroomId}/add-student/`,
        { user: userId, reading_level: readingLevel },
      );
      return User.fromServerUser(response.data);
    } catch (error) {
      console.error(error);
      return Promise.reject(new Error(parseError(error)));
    }
  };

  createStudent = async (
    student: TeacherGeneratedStudent,
    classroomId: string,
    subscription: Subscription,
  ): Promise<User> => {
    try {
      const userResponse = await apiService.api.post('/users/', {
        first_name: student.firstName,
        last_name: student.lastName,
        email: student.email,
        username: student.username,
        password: student.password,
        subscription_id: subscription.id.toString(),
      });
      const studentResponse = await this.addStudent(
        userResponse.data.user.id,
        classroomId,
        student.readingLevel,
      );
      return studentResponse;
    } catch (error) {
      console.error(error);
      return Promise.reject(new Error(parseError(error)));
    }
  };

  createPracticeAssignment = async (assignment: Assignment): Promise<any> => {
    try {
      const response = await apiService.api.post('/assignments/', {
        ...assignment,
      });
      return Assignment.fromServerAssignment(response.data.assignment);
    } catch (error) {
      console.error(error);
      return Promise.reject(new Error(parseError(error)));
    }
  };

  updatePracticeAssignment = async (
    assignmentId: string,
    assignment: Assignment,
  ): Promise<any> => {
    try {
      const response = await apiService.api.put(
        `/assignments/${assignmentId}/`,
        {
          ...assignment,
        },
      );
      return Assignment.fromServerAssignment(response.data.assignment);
    } catch (error) {
      console.error(error);
      return Promise.reject(new Error(parseError(error)));
    }
  };

  createModule = async (module: Module): Promise<any> => {
    try {
      const response = await apiService.api.post(`/modules/`, {
        ...module,
      });
      return Module.fromServerModule(response.data);
    } catch (error) {
      console.error(error);
      return Promise.reject(new Error(parseError(error)));
    }
  };

  updateModule = async (moduleId: string, module: Module): Promise<any> => {
    try {
      const response = await apiService.api.put(`/modules/${moduleId}/`, {
        ...module,
      });
      return Module.fromServerModule(response.data);
    } catch (error) {
      console.error(error);
      return Promise.reject(new Error(parseError(error)));
    }
  };

  deleteModule = async (moduleId: string): Promise<any> => {
    try {
      await apiService.api.delete(`/modules/${moduleId}/`);
      return moduleId;
    } catch (error) {
      console.error(error);
      return Promise.reject(new Error(parseError(error)));
    }
  };

  getUserQuestionResponses = async (
    submissionId: string,
  ): Promise<StudentQuestion[]> => {
    try {
      const response = await apiService.api.get(
        `/submissions/${submissionId}/questions`,
      );
      return response.data.questions.map((respQuestion: Record<string, any>) =>
        StudentQuestion.fromServerStudentQuestion({
          question: respQuestion,
          response: response.data.responses.find(
            (respObj: QuestionResponse) => respObj.question === respQuestion.id,
          ),
        }),
      );
    } catch (error) {
      console.error(error);
      return Promise.reject(new Error(parseError(error)));
    }
  };

  getSubmissionById = async (submissionId: string): Promise<any> => {
    try {
      const response = await apiService.api.get(
        `/submissions/${submissionId}/`,
      );

      const submission = AssignmentSubmission.fromServerAssignmentSubmission(
        response.data.assignmentsubmission,
      );

      return {
        submission: submission,
        questions: await this.getUserQuestionResponses(submissionId),
      };
    } catch (error) {
      console.error(error);
      return Promise.reject(new Error(parseError(error)));
    }
  };

  getMissedWords = async (
    known?: boolean,
    isKeyWord?: boolean,
    submissionId?: string,
    questionId?: string,
  ) => {
    try {
      // Build the query parameters object
      const queryParams: { [key: string]: string } = {};

      // Add parameters to the query if they are not null or undefined
      if (submissionId) queryParams['submission'] = submissionId;
      if (questionId) queryParams['question'] = questionId;
      if (known !== null && known !== undefined)
        queryParams['known'] = String(known);
      if (isKeyWord !== null && isKeyWord !== undefined)
        queryParams['isKeyWord'] = String(isKeyWord);

      // Convert the queryParams object into a query string
      const queryString = new URLSearchParams(queryParams).toString();

      // Make the API request with the dynamically constructed query string
      const response = await apiService.api.get(
        `/missed_words/?${queryString}`,
      );

      return response.data.map((mw: Record<string, any>) =>
        MissedWord.fromServerMissedWord(mw),
      );
    } catch (error) {
      console.error(error);
      return Promise.reject(new Error(parseError(error)));
    }
  };

  getAssignmentQuestions = async (
    assignmentId: string,
  ): Promise<Question[]> => {
    try {
      const response = await apiService.api.get(
        `/assignments/${assignmentId}/questions`,
      );

      const questions: Question[] = response.data.questions
        .map((q: Record<string, any>) => Question.fromServerQuestion(q))
        .sort((q1: Question, q2: Question) => q1.index - q2.index);

      // var responses = [];
      // for (const question of questions) {
      //   const res = await apiService.api.get(
      //     `/assignmentquestions/${question.id}/responses`,
      //   );
      //   responses.push(
      //     res.data.responses.map((r: Record<string, any>) =>
      //       QuestionResponse.fromServerQuestionResponse(r),
      //     ),
      //   );
      // }

      return questions;
    } catch (error) {
      console.error(error);
      return Promise.reject(new Error(parseError(error)));
    }
  };

  getQuestionResponses = async (
    questionId: string,
  ): Promise<QuestionResponse[]> => {
    try {
      const response = await apiService.api.get(
        `/assignmentquestions/${questionId}/responses`,
      );

      return response.data.responses.map((r: Record<string, any>) =>
        QuestionResponse.fromServerQuestionResponse(r),
      );
    } catch (error) {
      console.error(error);
      return Promise.reject(new Error(parseError(error)));
    }
  };

  getAssignmentFeedback = async (
    submissionId: string,
  ): Promise<AssignmentFeedback[]> => {
    try {
      const response = await apiService.api.get(
        `/submissions/${submissionId}/feedback`,
      );

      return response.data.map((feedbackData: Record<string, any>) =>
        AssignmentFeedback.fromServerFeedback(feedbackData),
      );
    } catch (error) {
      console.error(error);
      return Promise.reject(new Error(parseError(error)));
    }
  };

  gradeAssignmentSubmission = async (
    submission: AssignmentSubmission,
  ): Promise<{
    submission: AssignmentSubmission;
    feedback: Record<string, any>[];
  }> => {
    try {
      const response = await apiService.api.patch(
        `/submissions/${submission.id}/`,
        {
          grade: submission.grade,
          completion_score: submission.completion_score,
          correctness_score: submission.correctness_score,
          key_word_accuracy_score: submission.key_word_accuracy_score,
        },
      );

      return {
        submission: AssignmentSubmission.fromServerAssignmentSubmission(
          response.data.submission,
        ),
        feedback: response.data.feedback ?? [],
      };
    } catch (error) {
      console.error(error);
      return Promise.reject(new Error(parseError(error)));
    }
  };

  createAssignmentFeedback = async (
    feedback: AssignmentFeedback,
  ): Promise<AssignmentFeedback> => {
    try {
      const response = await apiService.api.post(`/assignment-feedback/`, {
        ...feedback.toJSON(),
      });

      return AssignmentFeedback.fromServerFeedback(response.data);
    } catch (error) {
      console.error(error);
      return Promise.reject(new Error(parseError(error)));
    }
  };

  updateAssignmentFeedback = async (
    feedback: AssignmentFeedback,
  ): Promise<AssignmentFeedback> => {
    try {
      const response = await apiService.api.patch(
        `/assignment-feedback/${feedback.id}/`,
        {
          ...feedback.toJSON(),
        },
      );

      return AssignmentFeedback.fromServerFeedback(response.data);
    } catch (error) {
      console.error(error);
      return Promise.reject(new Error(parseError(error)));
    }
  };

  deleteAssignmentFeedback = async (
    feedbackId: string,
  ): Promise<AssignmentFeedback> => {
    try {
      const response = await apiService.api.delete(
        `/assignment-feedback/${feedbackId}/`,
      );

      return response.data;
    } catch (error) {
      console.error(error);
      return Promise.reject(new Error(parseError(error)));
    }
  };
}

const teacherService = TeacherService.get();
export default teacherService;
